import { AnalyticsProvider } from '@inboundx/shared/analytics';
import { createSessionManager, FetchNetworkAdapter, logger } from '@inboundx/shared/platform';
import { DisplayController, type RoseWidgetRef } from '@inboundx/shared/widget';
import {
    AlertTriangle,
    Bot,
    ExternalLink,
    Eye,
    EyeOff,
    FileText,
    MousePointer,
    PartyPopper,
    Settings,
    Sparkles,
    TestTube,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ConfigurableStorageAdapter } from '../adapters/ConfigurableStorageAdapter';
import { getConfig, getConfigSummary, getCustomSiteUrl, shouldSkipIframe } from '../config';
import { API_ENDPOINTS } from '../constants/endpoints';
import { useAppContext } from '../contexts/AppContext';
import { AuthButton } from './AuthButton';
import { ClientConfigProvider } from './ClientConfigProvider';
import ConfigPanel from './ConfigPanel';
import { isPreprodParentWindowEventsEnabled } from './ConfigurationTab';
import { EventViewerPanel } from './EventViewerPanel';
import { getPreprodLlmResponseLanguageOverride } from './FeaturesTab';
import LanguagePicker, { type SupportedLanguage } from './LanguagePicker';
import { LanguageSyncFromConfig } from './LanguageSyncFromConfig';

/**
 * Wrapper component for EventViewerPanel
 * The panel visibility is controlled by the preprod toggle in the Features tab.
 * Events are always sent (parent window provider is enabled by default).
 */
function EventViewerWrapper() {
    // State for panel visibility - controlled by preprod toggle
    const [isVisible, setIsVisible] = useState(isPreprodParentWindowEventsEnabled);

    // Listen for localStorage changes (from config panel toggle)
    useEffect(() => {
        const handleStorageChange = () => {
            setIsVisible(isPreprodParentWindowEventsEnabled());
        };

        // Listen for storage events (from other tabs)
        window.addEventListener('storage', handleStorageChange);

        // Also poll periodically for same-tab changes (localStorage events don't fire for same tab)
        const interval = setInterval(handleStorageChange, 500);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            clearInterval(interval);
        };
    }, []);

    return <EventViewerPanel isVisible={isVisible} />;
}

function AppContent() {
    // Get context values
    const {
        selectedEndpoint,
        customEndpoint,
        useCustomEndpoint,
        selectedClient,
        availableClients,
        controlsHidden,
        setControlsHidden,
    } = useAppContext();

    // Navigation for test pages
    const navigate = useNavigate();

    // Language picker state
    const [selectedLanguage, setSelectedLanguage] = useState<SupportedLanguage>('en');

    // Debug language changes
    useEffect(() => {
        logger.debug('🌐 Language changed in MainApp:', selectedLanguage);
    }, [selectedLanguage]);

    // Note: PostHog readiness and initialization tracking are orchestrated by DisplayController
    // before RoseWidget renders, preserving event ordering guarantees in preprod.

    const [networkAdapter] = useState(new FetchNetworkAdapter());
    const [key, setKey] = useState(0); // Force re-render of widget
    const [showConfig, setShowConfig] = useState(false);
    const [, setAppConfig] = useState(getConfig());
    const [configSummary, setConfigSummary] = useState(getConfigSummary());
    const [apiHealthStatus, setApiHealthStatus] = useState<
        Record<string, { status: 'checking' | 'online' | 'offline'; version?: string }>
    >({});
    const [isRefreshingHealth, setIsRefreshingHealth] = useState(false);
    const [iframeError, setIframeError] = useState<string | null>(null);
    const [iframeLoaded, setIframeLoaded] = useState(false);
    const [showPlaceholder, setShowPlaceholder] = useState(false);
    const chatWidgetRef = useRef<RoseWidgetRef>(null);
    const [chatStateBeforeSettings, setChatStateBeforeSettings] = useState<
        'collapsed' | 'expanded' | 'minimized' | null
    >(null);
    const [isSmallScreen, setIsSmallScreen] = useState(false);

    // Derive conversation language from LLM override setting (stored in localStorage, synced via key bump)
    // biome-ignore lint/correctness/useExhaustiveDependencies: key is an intentional trigger — forces recalc when widget resets (reads from localStorage)
    const effectiveConversationLanguage = useMemo(() => {
        const llmOverride = getPreprodLlmResponseLanguageOverride();
        if (llmOverride === 'auto') return 'auto';
        if (llmOverride === 'site_language') return selectedLanguage;
        return undefined; // 'config' — let RoseWidget read from config
    }, [selectedLanguage, key]);

    // Get current API URL based on endpoint selection
    const getCurrentApiUrl = () => {
        if (useCustomEndpoint && customEndpoint) {
            return customEndpoint;
        }
        return selectedEndpoint?.url || API_ENDPOINTS[0].url;
    };

    // Create storage adapter that updates when endpoint changes
    // biome-ignore lint/correctness/useExhaustiveDependencies: getCurrentApiUrl closes over the listed deps — listing them instead of the unstable function reference
    const storageAdapter = useMemo(
        () => new ConfigurableStorageAdapter(getCurrentApiUrl),
        [selectedEndpoint, customEndpoint, useCustomEndpoint]
    );

    // Create session manager for proper session cleanup
    // biome-ignore lint/correctness/useExhaustiveDependencies: getCurrentApiUrl closes over the listed deps — listing them instead of the unstable function reference
    const sessionManager = useMemo(() => {
        return createSessionManager({
            siteName: selectedClient?.domain || 'localhost',
            apiUrl: getCurrentApiUrl(),
            storageAdapter: storageAdapter,
        });
    }, [
        selectedClient?.domain,
        selectedEndpoint,
        customEndpoint,
        useCustomEndpoint,
        storageAdapter,
    ]);

    // Reset conversation and widget when client changes
    const resetConversation = async (siteName: string) => {
        try {
            const apiUrl = getCurrentApiUrl();
            const config = getConfig();
            const headers: HeadersInit = {
                'Content-Type': 'application/json',
            };

            // Add API key header if available
            if (config.apiKey) {
                headers['X-API-Key'] = config.apiKey;
            }

            const response = await fetch(`${apiUrl}/api/lightrag/reset_memory`, {
                method: 'POST',
                mode: 'cors',
                headers,
                body: JSON.stringify({
                    siteName: siteName,
                    sessionId: 'default',
                }),
            });

            if (response.ok) {
                logger.debug(`Conversation reset for ${siteName}`);
            } else {
                console.warn(`Failed to reset conversation for ${siteName}:`, response.statusText);
            }
        } catch (error) {
            console.warn(`Error resetting conversation for ${siteName}:`, error);
        }
    };

    // Reset chat widget UI to initial state using SessionManager
    const resetChatWidget = async () => {
        try {
            await sessionManager.resetSession();
            logger.debug('Chat widget UI reset to initial state using SessionManager');
        } catch (error) {
            console.warn('Error resetting chat widget UI:', error);
        }
    };

    // Update the key when client or endpoint changes to force widget re-initialization
    // biome-ignore lint/correctness/useExhaustiveDependencies: getCurrentApiUrl/resetChatWidget/key are unstable — deps intentionally track the underlying values that trigger resets
    useEffect(() => {
        const resetForChange = async () => {
            logger.debug('🔄 Widget reset triggered by dependency change:', {
                selectedClient: selectedClient?.id,
                selectedEndpoint: selectedEndpoint?.id,
                customEndpoint,
                useCustomEndpoint,
                currentKey: key,
            });

            setKey((prev) => {
                const newKey = prev + 1;
                logger.debug('🔑 Widget key updated:', prev, '→', newKey);
                return newKey;
            });
            setIframeError(null); // Clear iframe error when changes occur
            setIframeLoaded(false);
            setShowPlaceholder(false);

            // Clear storage and reset chat widget when endpoint changes
            await resetChatWidget();
            if (chatWidgetRef.current) {
                chatWidgetRef.current.reset();
                logger.debug('🔄 Chat widget reset() called');
            }

            logger.debug('✅ Configuration changed, widget reset with new settings:', {
                apiUrl: getCurrentApiUrl(),
                key: key + 1,
            });
        };

        resetForChange();
    }, [selectedClient, selectedEndpoint, customEndpoint, useCustomEndpoint]);

    // Handle language changes separately - no need to reset widget, just update the prop
    useEffect(() => {
        logger.debug(
            '🌐 Language changed - widget will receive updated conversationLanguage prop:',
            selectedLanguage
        );
    }, [selectedLanguage]);

    // Reset conversation and widget when client changes
    const prevClientRef = useRef(selectedClient);
    // biome-ignore lint/correctness/useExhaustiveDependencies: resetChatWidget/resetConversation/sessionManager are unstable functions — effect intentionally runs only when selectedClient changes
    useEffect(() => {
        const resetForClientChange = async () => {
            if (
                prevClientRef.current &&
                selectedClient &&
                prevClientRef.current.id !== selectedClient.id
            ) {
                logger.debug(
                    `Client changed from ${prevClientRef.current.id} to ${selectedClient.id}, resetting conversation and widget`
                );

                // Update session manager with new site name
                sessionManager.updateConfig({
                    siteName: selectedClient?.domain || 'localhost',
                });

                // Use the chat widget's reset method for complete reset
                if (chatWidgetRef.current) {
                    chatWidgetRef.current.reset();
                }

                // Fallback: manual reset if ref not available
                if (!chatWidgetRef.current) {
                    await resetChatWidget();
                    resetConversation(selectedClient?.domain || 'default');
                }
            }
            prevClientRef.current = selectedClient;
        };

        resetForClientChange();
    }, [selectedClient]);

    // Health check function
    const checkApiHealth = async () => {
        // Check all predefined endpoints
        const endpointsToCheck = [...API_ENDPOINTS];

        // Add custom endpoint if it's being used
        if (useCustomEndpoint && customEndpoint) {
            endpointsToCheck.push({
                id: 'custom',
                name: 'Custom',
                url: customEndpoint,
            });
        }

        // Set all endpoints to checking status
        const checkingStatus: Record<
            string,
            { status: 'checking' | 'online' | 'offline'; version?: string }
        > = {};
        endpointsToCheck.forEach((endpoint) => {
            checkingStatus[endpoint.id] = { status: 'checking' };
        });
        setApiHealthStatus((prev) => ({ ...prev, ...checkingStatus }));

        // Check each endpoint in parallel
        const healthCheckPromises = endpointsToCheck.map(async (endpoint) => {
            try {
                const config = getConfig();
                const headers: Record<string, string> = {
                    Accept: 'application/json',
                };

                // Add API key if available
                if (config.apiKey) {
                    headers['X-API-Key'] = config.apiKey;
                }

                const response = await fetch(`${endpoint.url}/health`, {
                    method: 'GET',
                    mode: 'cors',
                    headers,
                    // Add timeout to prevent hanging
                    signal: AbortSignal.timeout(10000), // 10 second timeout
                }).catch((error) => {
                    // Silently handle connection errors during health checks
                    logger.debug(
                        `Health check connection failed for ${endpoint.name}`,
                        error.message
                    );
                    return null;
                });

                if (response?.ok) {
                    const data = await response.json().catch(() => ({}));
                    return {
                        id: endpoint.id,
                        status: 'online' as const,
                        version: data.version || 'Unknown',
                    };
                } else {
                    return {
                        id: endpoint.id,
                        status: 'offline' as const,
                    };
                }
            } catch (error) {
                // Silently handle health check failures - this is expected when services are down
                logger.debug(`Health check failed for ${endpoint.name}:`, error);
                return {
                    id: endpoint.id,
                    status: 'offline' as const,
                };
            }
        });

        // Process results as they come in
        const results = await Promise.allSettled(healthCheckPromises);

        results.forEach((result, index) => {
            const endpointId = endpointsToCheck[index].id;
            if (result.status === 'fulfilled') {
                setApiHealthStatus((prev) => ({
                    ...prev,
                    [endpointId]: result.value,
                }));
            } else {
                setApiHealthStatus((prev) => ({
                    ...prev,
                    [endpointId]: { status: 'offline' },
                }));
            }
        });
    };

    // Manual health check refresh
    const refreshHealthChecks = async () => {
        setIsRefreshingHealth(true);
        try {
            await checkApiHealth();
        } finally {
            setIsRefreshingHealth(false);
        }
    };

    // Check API health for all endpoints
    // biome-ignore lint/correctness/useExhaustiveDependencies: checkApiHealth is unstable; customEndpoint/useCustomEndpoint are intentional triggers to re-run health checks
    useEffect(() => {
        checkApiHealth();
        // Refresh every 30 seconds
        const interval = setInterval(checkApiHealth, 30000);
        return () => clearInterval(interval);
    }, [customEndpoint, useCustomEndpoint]);

    // Simple screen size detection for preprod UI
    useEffect(() => {
        const handleResize = () => {
            const isSmall = window.innerWidth < 768;
            setIsSmallScreen(isSmall);
            logger.debug(`PreProd: Screen size changed - small screen (< 768px): ${isSmall}`);
        };

        handleResize(); // Set initial value
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    // Note: PostHog readiness checking is handled by AnalyticsProvider + DisplayController
    // before the widget is allowed to render.

    // Handle configuration changes
    const handleConfigChange = () => {
        setAppConfig(getConfig());
        setConfigSummary(getConfigSummary());
        setKey((prev) => prev + 1); // Force widget re-render with new config
    };

    const getCurrentEndpointKey = () => {
        return useCustomEndpoint ? 'custom' : selectedEndpoint?.id || API_ENDPOINTS[0].id;
    };

    // List of known domains that typically have CSP frame-ancestors restrictions
    const knownCSPBlockedDomains = [
        'abtasty.com',
        'skello.io',
        'github.com',
        'google.com',
        'facebook.com',
        'twitter.com',
        'linkedin.com',
        'microsoft.com',
    ];

    // Check if URL is likely to be CSP blocked
    const isLikelyCSPBlocked = (url: string) => {
        try {
            const domain = new URL(url).hostname.replace('www.', '');
            return knownCSPBlockedDomains.some((blocked) => domain.includes(blocked));
        } catch {
            return false;
        }
    };

    // Check if URL should skip iframe (either CSP blocked or user configured)
    const shouldSkipIframeLoading = (url: string) => {
        // If there's a custom URL, we should try to load it
        if (selectedClient) {
            const customUrl = getCustomSiteUrl(selectedClient.id, selectedClient.domain);
            if (customUrl) {
                return false; // Don't skip iframe if we have a custom URL
            }
        }
        return isLikelyCSPBlocked(url) || shouldSkipIframe(url);
    };

    // Handle iframe load success
    const handleIframeLoad = () => {
        setIframeLoaded(true);
        setShowPlaceholder(false);
    };

    // Handle iframe load errors
    const handleIframeError = () => {
        setIframeLoaded(false);
        if (selectedClient?.isLocalPreview) {
            setIframeError(
                `Cannot load local preview from ${selectedClient.websiteUrl}. Please check that the local assets are accessible.`
            );
        } else {
            // Show placeholder for CSP errors if no local preview URL
            setShowPlaceholder(true);
        }
    };

    // Listen for CSP violations and security errors
    // biome-ignore lint/correctness/useExhaustiveDependencies: shouldSkipIframeLoading is an unstable function — its underlying deps (selectedClient) are already tracked
    useEffect(() => {
        const handleSecurityError = (event: ErrorEvent) => {
            // Check if this is a CSP frame-ancestors violation
            if (
                event.message?.includes('frame-ancestors') ||
                event.message?.includes('Refused to frame')
            ) {
                console.warn('CSP frame-ancestors violation detected:', event.message);
                if (!selectedClient?.isLocalPreview) {
                    setShowPlaceholder(true);
                }
            }
        };

        const handleCSPViolation = (event: SecurityPolicyViolationEvent) => {
            if (
                event.violatedDirective === 'frame-ancestors' ||
                event.blockedURI === selectedClient?.websiteUrl
            ) {
                console.warn('CSP violation detected:', event);
                if (!selectedClient?.isLocalPreview) {
                    setShowPlaceholder(true);
                }
            }
        };

        // Listen for both error events and CSP violations
        window.addEventListener('error', handleSecurityError);
        window.addEventListener('securitypolicyviolation', handleCSPViolation);

        // For sites that should skip iframe (CSP-blocked or user configured), show placeholder immediately
        // For others, use a longer timeout as fallback to allow more time for iframe loading
        const shouldSkip = shouldSkipIframeLoading(selectedClient?.websiteUrl || '');
        const timeoutDuration = shouldSkip ? 100 : 5000; // 100ms for sites to skip, 5s for others

        const timer = setTimeout(() => {
            if (!selectedClient?.isLocalPreview && !iframeLoaded && !showPlaceholder) {
                console.warn(
                    `Iframe failed to load ${selectedClient?.websiteUrl} - likely CSP restriction`
                );
                setShowPlaceholder(true);
            }
        }, timeoutDuration);

        return () => {
            window.removeEventListener('error', handleSecurityError);
            window.removeEventListener('securitypolicyviolation', handleCSPViolation);
            clearTimeout(timer);
        };
    }, [selectedClient, iframeLoaded, showPlaceholder]);

    // Open website in new tab
    const openInNewTab = () => {
        window.open(selectedClient?.websiteUrl || '', '_blank', 'noopener,noreferrer');
    };

    // Show configuration prompt if no clients are available
    if (availableClients.length === 0 && !selectedClient) {
        return (
            <div
                style={{
                    height: '100vh',
                    width: '100vw',
                    overflow: 'hidden',
                    position: 'relative',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                }}
            >
                {/* Configuration Needed Message */}
                <div
                    className="config-required-screen"
                    data-ph-capture-attribute-screen-type="configuration-required"
                    style={{
                        width: '100%',
                        height: '100%',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        textAlign: 'center',
                        padding: '2rem',
                    }}
                >
                    <div className="config-required-card bg-white/10 backdrop-blur-sm rounded-xl p-8 max-w-md w-full mx-auto border border-white/20">
                        <div className="config-required-icon-container w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
                            <Settings className="h-8 w-8 text-white" />
                        </div>
                        <h2 className="config-required-title text-2xl font-bold text-white mb-4">
                            Configuration Required
                        </h2>
                        <p className="config-required-description text-white/90 mb-6 leading-relaxed">
                            No API endpoint is configured or accessible. Please configure your API
                            endpoint and ensure it's running to access available client websites.
                        </p>
                        <Button
                            onClick={() => setShowConfig(true)}
                            className="config-required-settings-button w-full bg-white text-purple-600 hover:bg-white/90 font-semibold py-3 px-6 text-lg"
                            data-ph-capture-attribute-button-type="open-settings"
                            data-ph-capture-attribute-context="configuration-required"
                        >
                            <Settings className="mr-2 h-5 w-5" />
                            Open Settings
                        </Button>
                        <p className="config-required-help-text text-white/70 text-sm mt-4">
                            Configure your API endpoint in the settings panel to get started.
                        </p>
                    </div>
                </div>

                {/* Top Controls - Settings Button */}
                <div className="top-controls-container fixed top-6 right-6 z-[999] flex items-center gap-3">
                    {/* Language Picker */}
                    <LanguagePicker
                        selectedLanguage={selectedLanguage}
                        onLanguageChange={setSelectedLanguage}
                        className="bg-black/70 backdrop-blur-sm rounded-lg border border-white/20"
                    />
                    <Button
                        onClick={() => setShowConfig(true)}
                        size="icon"
                        className="settings-floating-button w-12 h-12 rounded-full border-2 border-white/80 bg-black/70 text-white hover:bg-black/80 backdrop-blur-sm shadow-lg hover:scale-110 transition-all duration-300"
                        title="Settings - Configure API Endpoint"
                        data-ph-capture-attribute-button-type="settings"
                        data-ph-capture-attribute-location="floating-header"
                        data-ph-capture-attribute-context="config-required"
                    >
                        <Settings className="h-5 w-5" />
                    </Button>
                </div>

                {/* Configuration Panel */}
                <ConfigPanel
                    isOpen={showConfig}
                    onClose={() => setShowConfig(false)}
                    onConfigChange={handleConfigChange}
                    apiHealthStatus={apiHealthStatus}
                    configSummary={configSummary}
                    onRefreshHealthChecks={refreshHealthChecks}
                    isRefreshingHealth={isRefreshingHealth}
                />
            </div>
        );
    }

    return (
        <div
            style={{
                height: '100vh',
                width: '100vw',
                overflow: 'hidden',
                position: 'relative',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            }}
        >
            {/* Website iframe - Full screen */}
            {!iframeError &&
            !showPlaceholder &&
            !shouldSkipIframeLoading(selectedClient?.websiteUrl || '') ? (
                <iframe
                    src={(() => {
                        // Check for custom URL configuration
                        if (selectedClient) {
                            const customUrl = getCustomSiteUrl(
                                selectedClient.id,
                                selectedClient.domain
                            );
                            if (customUrl) {
                                return customUrl;
                            }
                        }
                        logger.debug('Using original URL:', selectedClient?.websiteUrl);
                        return selectedClient?.websiteUrl || '';
                    })()}
                    style={{
                        width: '100%',
                        height: '100%',
                        border: 'none',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                    }}
                    title="Website Preview"
                    sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
                    onLoad={handleIframeLoad}
                    onError={handleIframeError}
                />
            ) : showPlaceholder || shouldSkipIframeLoading(selectedClient?.websiteUrl || '') ? (
                /* Placeholder page for CSP-blocked sites */
                <div
                    style={{
                        width: '100%',
                        height: '100vh',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                        overflowY: 'auto',
                        color: '#374151',
                        textAlign: 'center',
                        padding: '2rem',
                    }}
                >
                    {/* Main content - centered */}
                    <div style={{ paddingTop: '10vh', paddingBottom: '2rem' }}>
                        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full mx-auto">
                            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <ExternalLink className="h-8 w-8 text-blue-600" />
                            </div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-3">
                                {selectedClient?.name || 'Unknown'}
                            </h2>
                            <p className="text-gray-600 mb-4">Website Preview Placeholder</p>
                            <p className="text-sm text-gray-500 mb-6">
                                {shouldSkipIframe(selectedClient?.websiteUrl || '')
                                    ? 'Iframe loading is disabled for this site in the configuration. You can still test the chat widget functionality on this page.'
                                    : 'This website cannot be embedded due to security restrictions (CSP frame-ancestors policy). You can still test the chat widget functionality on this page.'}
                            </p>
                            <div className="space-y-3">
                                <Button
                                    onClick={openInNewTab}
                                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg"
                                >
                                    <ExternalLink className="mr-2 h-4 w-4" />
                                    Visit {selectedClient?.name || 'Website'}
                                </Button>
                                <p className="text-xs text-gray-400">
                                    Original URL: {selectedClient?.websiteUrl || ''}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Scrollable test content */}
                    <div className="max-w-4xl mx-auto mt-12 space-y-8 pb-20">
                        <div className="bg-white/60 backdrop-blur rounded-lg p-8">
                            <h3 className="text-xl font-semibold text-gray-800 mb-4">
                                Testing Scroll-Based Widget Visibility
                            </h3>
                            <p className="text-gray-600 mb-4 leading-relaxed">
                                This page has scrollable content to test the chat widget's
                                scroll-based visibility feature. On small viewports (&lt;900px
                                height), the widget should appear after you scroll 150px down.
                            </p>
                            <p className="text-gray-500 text-sm">
                                Scroll down to see more content and test the widget appearance.
                            </p>
                        </div>

                        {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                            <div
                                key={i}
                                className="bg-white/40 backdrop-blur rounded-lg p-8 shadow-sm min-h-[200px]"
                            >
                                <h4 className="text-lg font-semibold text-gray-700 mb-3">
                                    Test Section {i}
                                </h4>
                                <p className="text-gray-600 leading-relaxed mb-3">
                                    Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do
                                    eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut
                                    enim ad minim veniam, quis nostrud exercitation ullamco laboris
                                    nisi ut aliquip ex ea commodo consequat.
                                </p>
                                <p className="text-gray-500 text-sm">
                                    Duis aute irure dolor in reprehenderit in voluptate velit esse
                                    cillum dolore eu fugiat nulla pariatur. Keep scrolling to test
                                    the widget visibility behavior on small viewports.
                                </p>
                            </div>
                        ))}

                        <div className="bg-green-50 border-2 border-green-200 rounded-lg p-8 min-h-[150px]">
                            <p className="text-green-800 font-medium text-lg mb-2">
                                ✅ Scroll Test Complete!
                            </p>
                            <p className="text-green-700">
                                If you scrolled this far on a small viewport (&lt;900px height), the
                                chat widget should now be visible at the bottom of the page!
                            </p>
                        </div>
                    </div>
                </div>
            ) : (
                <div
                    style={{
                        width: '100%',
                        height: '100vh',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        overflowY: 'auto',
                        color: 'white',
                        textAlign: 'center',
                        padding: '2rem',
                    }}
                >
                    {/* Main error message - centered */}
                    <div style={{ paddingTop: '10vh', paddingBottom: '2rem' }}>
                        <AlertTriangle size={64} className="mb-4 text-yellow-400 mx-auto" />
                        <h2 className="text-2xl font-bold mb-4">
                            {selectedClient?.isLocalPreview
                                ? 'Local Preview Unavailable'
                                : 'Website Cannot Be Embedded'}
                        </h2>
                        <p className="text-lg mb-6 max-w-2xl mx-auto leading-relaxed">
                            {iframeError}
                        </p>
                        <div className="flex gap-4 justify-center">
                            <Button
                                onClick={openInNewTab}
                                className="bg-white text-gray-900 hover:bg-gray-100 font-semibold px-6 py-3 text-lg"
                            >
                                <ExternalLink className="mr-2 h-5 w-5" />
                                {selectedClient?.isLocalPreview
                                    ? `View ${selectedClient?.name || 'Website'} Assets`
                                    : `Open ${selectedClient?.name || 'Website'} in New Tab`}
                            </Button>
                            <Button
                                onClick={() => setIframeError(null)}
                                variant="outline"
                                className="border-white text-white hover:bg-white hover:text-gray-900 font-semibold px-6 py-3 text-lg"
                            >
                                Try Again
                            </Button>
                        </div>
                        <p className="text-sm opacity-75 mt-6">
                            You can still test the chat widget functionality on this page
                        </p>
                    </div>

                    {/* Scrollable test content */}
                    <div className="max-w-4xl mx-auto mt-12 space-y-8 pb-20">
                        <div className="bg-white/20 backdrop-blur rounded-lg p-8">
                            <h3 className="text-xl font-semibold text-white mb-4">
                                Testing Scroll-Based Widget Visibility
                            </h3>
                            <p className="text-white/90 mb-4 leading-relaxed">
                                This page has scrollable content to test the chat widget's
                                scroll-based visibility feature. On small viewports (&lt;900px
                                height), the widget should appear after you scroll 150px down.
                            </p>
                            <p className="text-white/70 text-sm">
                                Scroll down to see more content and test the widget appearance.
                            </p>
                        </div>

                        {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                            <div
                                key={i}
                                className="bg-white/10 backdrop-blur rounded-lg p-8 shadow-sm min-h-[200px]"
                            >
                                <h4 className="text-lg font-semibold text-white mb-3">
                                    Test Section {i}
                                </h4>
                                <p className="text-white/80 leading-relaxed mb-3">
                                    Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do
                                    eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut
                                    enim ad minim veniam, quis nostrud exercitation ullamco laboris
                                    nisi ut aliquip ex ea commodo consequat.
                                </p>
                                <p className="text-white/60 text-sm">
                                    Duis aute irure dolor in reprehenderit in voluptate velit esse
                                    cillum dolore eu fugiat nulla pariatur. Keep scrolling to test
                                    the widget visibility behavior on small viewports.
                                </p>
                            </div>
                        ))}

                        <div className="bg-green-500/20 border-2 border-green-300/50 rounded-lg p-8 min-h-[150px]">
                            <p className="text-white font-medium text-lg mb-2">
                                ✅ Scroll Test Complete!
                            </p>
                            <p className="text-white/90">
                                If you scrolled this far on a small viewport (&lt;900px height), the
                                chat widget should now be visible at the bottom of the page!
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Top Controls */}
            <div
                className="main-top-controls fixed top-6 right-6 z-[999] flex items-center gap-3"
                data-ph-capture-attribute-section="header-controls"
            >
                {!controlsHidden && (
                    <>
                        {/* Test Pages Menu */}
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    size="icon"
                                    className="w-12 h-12 rounded-full border-2 border-white/80 bg-black/70 text-white hover:bg-black/80 backdrop-blur-sm shadow-lg hover:scale-110 transition-all duration-300"
                                    title="Test Pages"
                                >
                                    <TestTube className="h-5 w-5" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                                align="end"
                                className="w-48 bg-white border border-gray-200 shadow-lg z-[9999]"
                            >
                                <DropdownMenuItem
                                    onClick={() => navigate('/ai-sections')}
                                    className="cursor-pointer hover:bg-gray-100"
                                >
                                    <Sparkles className="mr-2 h-4 w-4" />
                                    AI Sections
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                    onClick={() => navigate('/cta-test')}
                                    className="cursor-pointer hover:bg-gray-100"
                                >
                                    <MousePointer className="mr-2 h-4 w-4" />
                                    CTA Test
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                    onClick={() => navigate('/form-test')}
                                    className="cursor-pointer hover:bg-gray-100"
                                >
                                    <FileText className="mr-2 h-4 w-4" />
                                    Form Test
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                    onClick={() => navigate('/form-assistant')}
                                    className="cursor-pointer hover:bg-gray-100"
                                >
                                    <Bot className="mr-2 h-4 w-4" />
                                    Form Assistant
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                    onClick={() => navigate('/thank-you')}
                                    className="cursor-pointer hover:bg-gray-100"
                                >
                                    <PartyPopper className="mr-2 h-4 w-4" />
                                    Post-Conversion
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>

                        {/* Language Picker */}
                        <LanguagePicker
                            selectedLanguage={selectedLanguage}
                            onLanguageChange={setSelectedLanguage}
                            className="bg-black/70 backdrop-blur-sm rounded-lg border border-white/20"
                        />
                        <AuthButton />
                        <Button
                            onClick={() => {
                                // Save current chat state before opening settings
                                if (chatWidgetRef.current) {
                                    setChatStateBeforeSettings(chatWidgetRef.current.getState());
                                    // Minimize chat when opening settings
                                    if (chatWidgetRef.current.getState() === 'expanded') {
                                        chatWidgetRef.current.minimize();
                                    }
                                }
                                setShowConfig(true);
                            }}
                            size="icon"
                            className="main-settings-button w-12 h-12 rounded-full border-2 border-white/80 bg-black/70 text-white hover:bg-black/80 backdrop-blur-sm shadow-lg hover:scale-110 transition-all duration-300"
                            title="Settings"
                            data-ph-capture-attribute-button-type="settings"
                            data-ph-capture-attribute-location="main-header"
                            data-ph-capture-attribute-api-version={
                                apiHealthStatus[getCurrentEndpointKey()]?.version
                            }
                            data-ph-capture-attribute-selected-client={selectedClient?.id}
                        >
                            <Settings className="h-5 w-5" />
                        </Button>
                    </>
                )}

                {/* Toggle Controls Visibility */}
                <Button
                    onClick={() => setControlsHidden(!controlsHidden)}
                    size="icon"
                    className="w-12 h-12 rounded-full border-2 border-white/80 bg-black/70 text-white hover:bg-black/80 backdrop-blur-sm shadow-lg hover:scale-110 transition-all duration-300"
                    title={controlsHidden ? 'Show controls' : 'Hide controls'}
                >
                    {controlsHidden ? <Eye className="h-5 w-5" /> : <EyeOff className="h-5 w-5" />}
                </Button>
            </div>

            {/* Version Info */}
            {!controlsHidden && (
                <div className="fixed bottom-4 left-4 z-[999] flex items-center gap-2 bg-black/60 backdrop-blur-sm rounded-lg px-3 py-1.5 border border-white/10 text-[11px] font-mono text-white/70">
                    <span className="text-white/90">
                        {useCustomEndpoint ? 'Custom' : selectedEndpoint?.name || '?'}
                    </span>
                    <span className="text-white/30">|</span>
                    <span>
                        fe{' '}
                        <span className="text-white/90">
                            {typeof __INBOUNDX_VERSION__ !== 'undefined'
                                ? __INBOUNDX_VERSION__
                                : '?'}
                        </span>
                    </span>
                    <span className="text-white/30">|</span>
                    <span>
                        be{' '}
                        {apiHealthStatus[getCurrentEndpointKey()]?.status === 'online' ? (
                            <span className="text-white/90">
                                {apiHealthStatus[getCurrentEndpointKey()]?.version || '?'}
                            </span>
                        ) : (
                            <span className="text-red-400">offline</span>
                        )}
                    </span>
                </div>
            )}

            {/* Widget Component - Render if client is selected */}
            {(() => {
                const currentApiUrl = getCurrentApiUrl();

                if (isSmallScreen) {
                    logger.debug(
                        `PreProd: Small screen detected (< 768px) - showing mobile-responsive widget`
                    );
                }

                if (!selectedClient) {
                    logger.debug('PreProd: No client selected - widget not rendered');
                    return null;
                }

                return (
                    <ClientConfigProvider
                        client={selectedClient}
                        apiUrl={currentApiUrl}
                        apiKey={getConfig().apiKey}
                        forceApiUrl
                    >
                        <LanguageSyncFromConfig onLanguageDetected={setSelectedLanguage} />
                        <AnalyticsProvider>
                            <EventViewerWrapper />
                            <DisplayController
                                key={key}
                                ref={chatWidgetRef}
                                apiUrl={currentApiUrl}
                                apiKey={getConfig().apiKey}
                                forceApiUrl
                                siteName={selectedClient.siteName}
                                domain={selectedClient.domain}
                                storageAdapter={storageAdapter}
                                networkAdapter={networkAdapter}
                                forceDisplay={true}
                                conversationLanguage={effectiveConversationLanguage}
                                visitorIp={getConfig().visitorIp}
                            />
                        </AnalyticsProvider>
                    </ClientConfigProvider>
                );
            })()}

            {/* Configuration Panel */}
            <ConfigPanel
                isOpen={showConfig}
                onClose={() => {
                    setShowConfig(false);
                    // Restore chat state when closing settings
                    if (chatWidgetRef.current && chatStateBeforeSettings) {
                        switch (chatStateBeforeSettings) {
                            case 'expanded':
                                chatWidgetRef.current.expand();
                                break;
                            case 'collapsed':
                                chatWidgetRef.current.collapse();
                                break;
                            case 'minimized':
                                chatWidgetRef.current.minimize();
                                break;
                        }
                        setChatStateBeforeSettings(null);
                    }
                }}
                onConfigChange={handleConfigChange}
                apiHealthStatus={apiHealthStatus}
                configSummary={configSummary}
                onRefreshHealthChecks={refreshHealthChecks}
                isRefreshingHealth={isRefreshingHealth}
            />
        </div>
    );
}

// MainApp is now just the content - AuthGuard and AppProvider are in App.tsx
export default function MainApp() {
    return <AppContent />;
}
